// src/SideWinder.Web/src/composables/useStrikeAnchor.ts
//
// Task 21/27 of docs/superpowers/plans/2026-08-20-strike-anchor-multifund.md
//
// Reactive pre/post/degraded state machine driving GoldSignalPanel
// (Task 22) and GgeSignalPanel (Task 23)'s strike-anchor badges. Reads
// strikeState + strikeAtUtc + returnFrac from the fund's kt-latest
// endpoint (GoldKtSynthNavPublisherService / GgeKtSynthNavPublisherService
// payload — see api/client.ts's GoldKtLatestResponse doc for the 3-branch
// contract: 'pre' = no correction yet, raw daily r_kt; 'post' = the
// noon-UK basket is frozen, drift-from-strike is being reported; 'degraded'
// = past strike but no anchor captured — falls back to raw daily r_kt with
// a WARN badge).
//
// This composable ONLY drives DISPLAY state — it does NOT decide alert
// routing (Task 20's HealthMonitorService strike_anchor_missing /
// strike_anchor_ccy_stale / strike_anchor_feed_stale checks own that).
//
// Fetch pattern: deliberately NOT gated behind onMounted/onUnmounted — the
// composable fetches eagerly on call (same convention as
// useFundEnablement.ts's fetchEnablement()), so a bare
// `useStrikeAnchor('gold')` call in a unit test (no component instance)
// still triggers the load without needing a mount harness. Polling still
// uses onScopeDispose for cleanup when called inside a component's setup()
// (an active effect scope); outside one (e.g. a plain composable-level
// test) it's a documented no-op — the interval simply outlives the test,
// which is harmless because vi.useFakeTimers()/module teardown between
// test files resets it.
//
// GGE wiring note (Task 21 self-review — see task-21-report.md): api.
// ggeKtLatest() calls /api/gge/kt-latest. Task 21 flagged that route as
// unregistered; Task 23 registered it in Program.cs (mirrors
// /gold/kt-latest), so useStrikeAnchor('gge') now resolves against a live
// endpoint. The failure-swallowing load() behaviour described below still
// applies unchanged for genuine outages (network error, Redis down, etc).

import { ref, computed, getCurrentScope, onScopeDispose, type Ref, type ComputedRef } from 'vue'
import { api, type GoldKtLatestResponse } from '../api/client'

export type StrikeState = 'pre' | 'post' | 'degraded'
export type StrikeBadge = 'none' | 'green' | 'amber'

// Mirrors GoldSignalPanel.vue's existing K_t poll cadence (ktTimer,
// 300_000ms) — the publisher fires once at 07:35 UK and then every 60s
// while strikeState=post (RunLoopAsync), so a 5-minute client poll is well
// inside the panel's "at most one producer tick behind" tolerance.
const POLL_INTERVAL_MS = 300_000

/**
 * Formats a UTC ISO timestamp as "HH:MM" in Europe/London local time
 * (BST/GMT auto-handled by the runtime's tz database — same approach as
 * GoldSignalPanel.vue's londonNow() / DealingPlanCard.vue).
 */
function formatUkTime(isoUtc: string): string | null {
  const d = new Date(isoUtc)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d)
  const map: Record<string, string> = {}
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value
  if (!map.hour || !map.minute) return null
  return `${map.hour}:${map.minute}`
}

export interface UseStrikeAnchorResult {
  strikeState: Ref<StrikeState>
  returnPct: Ref<number | null>
  badge: ComputedRef<StrikeBadge>
  badgeText: ComputedRef<string>
  strikeAtUtc: Ref<string | null>
}

export function useStrikeAnchor(fundId: 'gold' | 'gge'): UseStrikeAnchorResult {
  const strikeState = ref<StrikeState>('pre')
  const returnPct = ref<number | null>(null)
  const strikeAtUtc = ref<string | null>(null)

  async function load(): Promise<void> {
    let resp: GoldKtLatestResponse
    try {
      resp = fundId === 'gge' ? await api.ggeKtLatest() : await api.goldKtLatest()
    } catch {
      // Network error / 404 (see GGE wiring note above) — keep last known
      // state rather than flashing the panel back to an unearned 'pre'.
      return
    }
    if (!resp?.available) return

    strikeState.value = resp.strikeState === 'post' || resp.strikeState === 'degraded'
      ? resp.strikeState
      : 'pre'
    returnPct.value = resp.returnFrac != null ? resp.returnFrac * 100 : null
    strikeAtUtc.value = resp.strikeAtUtc ?? null
  }

  void load()
  const timer = setInterval(() => { void load() }, POLL_INTERVAL_MS)
  // Only register cleanup inside an active effect scope (real component
  // setup()) — calling onScopeDispose() with none active is a no-op but
  // logs a dev warning, which a bare composable-level unit test would
  // trigger on every case. See file doc comment.
  if (getCurrentScope()) onScopeDispose(() => clearInterval(timer))

  const badge = computed<StrikeBadge>(() => {
    if (strikeState.value === 'post') return 'green'
    if (strikeState.value === 'degraded') return 'amber'
    return 'none'
  })

  const badgeText = computed<string>(() => {
    if (strikeState.value === 'post') {
      const t = strikeAtUtc.value ? formatUkTime(strikeAtUtc.value) : null
      return `Post-strike drift since ${t ?? '12:00'} UK`
    }
    if (strikeState.value === 'degraded') return 'NO STRIKE ANCHOR — degraded'
    return ''
  })

  return { strikeState, returnPct, badge, badgeText, strikeAtUtc }
}
